import { type RefObject, useLayoutEffect, useRef, useState } from "react";

export type ViewportAlign = "start" | "end";
export type ViewportSide = "above" | "below";

export const VIEWPORT_OBSTACLE_ATTR = "data-viewport-obstacle";

export type UseViewportAwarePlacementOptions = {
    active: boolean;
    preferredAlign: ViewportAlign;
    preferredSide: ViewportSide;
    paddingPx?: number;
    gapPx?: number;
};

export type UseViewportAwarePlacementResult = {
    ref: RefObject<HTMLDivElement | null>;
    align: ViewportAlign;
    side: ViewportSide;
    offset: { x: number; y: number };
};

export type ViewportBounds = {
    top: number;
    left: number;
    bottom: number;
    right: number;
};

const DEFAULT_PADDING_PX = 8;
const DEFAULT_GAP_PX = 8;
const OBSTACLE_EDGE_THRESHOLD_PX = 24;
const ZERO_OFFSET = { x: 0, y: 0 } as const;

export function useViewportAwarePlacement({
    active,
    preferredAlign,
    preferredSide,
    paddingPx = DEFAULT_PADDING_PX,
    gapPx = DEFAULT_GAP_PX,
}: UseViewportAwarePlacementOptions): UseViewportAwarePlacementResult {
    const ref = useRef<HTMLDivElement>(null);
    const [align, setAlign] = useState(preferredAlign);
    const [side, setSide] = useState(preferredSide);
    const [offset, setOffset] = useState<{ x: number; y: number }>(ZERO_OFFSET);

    useLayoutEffect(() => {
        if (!active) {
            setAlign(preferredAlign);
            setSide(preferredSide);
            setOffset(ZERO_OFFSET);
            return;
        }

        function update() {
            const element = ref.current;
            if (!element) {
                return;
            }

            const parent = element.offsetParent;
            if (!(parent instanceof HTMLElement)) {
                return;
            }

            const width = element.offsetWidth;
            const height = element.offsetHeight;
            const anchor = parent.getBoundingClientRect();
            const bounds = getAvailableViewportBounds(element, paddingPx);

            const startLeft = anchor.left;
            const endLeft = anchor.right - width;
            const startOverflow = Math.max(0, startLeft + width - bounds.right);
            const endOverflow = Math.max(0, bounds.left - endLeft);

            let nextAlign = preferredAlign;
            if (
                preferredAlign === "start" &&
                startOverflow > 0 &&
                endOverflow < startOverflow
            ) {
                nextAlign = "end";
            } else if (
                preferredAlign === "end" &&
                endOverflow > 0 &&
                startOverflow < endOverflow
            ) {
                nextAlign = "start";
            }

            const aboveTop = anchor.top - gapPx - height;
            const belowTop = anchor.bottom + gapPx;
            const aboveOverflow = Math.max(0, bounds.top - aboveTop);
            const belowOverflow = Math.max(0, belowTop + height - bounds.bottom);

            let nextSide = preferredSide;
            if (
                preferredSide === "above" &&
                aboveOverflow > 0 &&
                belowOverflow < aboveOverflow
            ) {
                nextSide = "below";
            } else if (
                preferredSide === "below" &&
                belowOverflow > 0 &&
                aboveOverflow < belowOverflow
            ) {
                nextSide = "above";
            }

            const left = nextAlign === "start" ? startLeft : endLeft;
            const top = nextSide === "above" ? aboveTop : belowTop;

            let x = 0;
            let y = 0;
            if (left < bounds.left) {
                x = bounds.left - left;
            } else if (left + width > bounds.right) {
                x = bounds.right - (left + width);
            }

            if (top < bounds.top) {
                y = bounds.top - top;
            } else if (top + height > bounds.bottom) {
                y = bounds.bottom - (top + height);
            }

            setAlign((current) => (current === nextAlign ? current : nextAlign));
            setSide((current) => (current === nextSide ? current : nextSide));
            setOffset((current) =>
                current.x === x && current.y === y ? current : { x, y },
            );
        }

        update();
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);

        const resizeObserver = new ResizeObserver(update);
        for (const obstacle of Array.from(
            document.querySelectorAll(`[${VIEWPORT_OBSTACLE_ATTR}]`),
        )) {
            resizeObserver.observe(obstacle);
        }
        if (ref.current) {
            resizeObserver.observe(ref.current);
        }

        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
            resizeObserver.disconnect();
        };
    }, [active, preferredAlign, preferredSide, paddingPx, gapPx]);

    return { ref, align, side, offset };
}

export function getAvailableViewportBounds(
    element: HTMLElement,
    paddingPx: number,
): ViewportBounds {
    let top = paddingPx;
    let left = paddingPx;
    let bottom = window.innerHeight - paddingPx;
    let right = window.innerWidth - paddingPx;

    let ancestor: HTMLElement | null = element.parentElement;
    while (ancestor) {
        const style = getComputedStyle(ancestor);
        const clipsX = isClippingOverflow(style.overflowX);
        const clipsY = isClippingOverflow(style.overflowY);

        if (clipsX || clipsY) {
            const rect = ancestor.getBoundingClientRect();
            if (clipsY) {
                top = Math.max(top, rect.top + paddingPx);
                bottom = Math.min(bottom, rect.bottom - paddingPx);
            }
            if (clipsX) {
                left = Math.max(left, rect.left + paddingPx);
                right = Math.min(right, rect.right - paddingPx);
            }
        }

        ancestor = ancestor.parentElement;
    }

    for (const obstacle of Array.from(
        document.querySelectorAll(`[${VIEWPORT_OBSTACLE_ATTR}]`),
    )) {
        if (!(obstacle instanceof HTMLElement)) {
            continue;
        }

        const rect = obstacle.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            continue;
        }

        const intersects =
            rect.left < right &&
            rect.right > left &&
            rect.top < bottom &&
            rect.bottom > top;

        if (!intersects) {
            continue;
        }

        const dockedBottom = bottom - rect.bottom < OBSTACLE_EDGE_THRESHOLD_PX;
        const dockedTop = rect.top - top < OBSTACLE_EDGE_THRESHOLD_PX;
        const dockedLeft = rect.left - left < OBSTACLE_EDGE_THRESHOLD_PX;
        const dockedRight = right - rect.right < OBSTACLE_EDGE_THRESHOLD_PX;
        const isWide = rect.width >= rect.height;

        if (isWide) {
            if (dockedBottom) {
                bottom = Math.min(bottom, rect.top - paddingPx);
            } else if (dockedTop) {
                top = Math.max(top, rect.bottom + paddingPx);
            }
        } else if (dockedLeft) {
            left = Math.max(left, rect.right + paddingPx);
        } else if (dockedRight) {
            right = Math.min(right, rect.left - paddingPx);
        }
    }

    return { top, left, bottom, right };
}

function isClippingOverflow(value: string): boolean {
    return (
        value === "hidden" || value === "auto" || value === "scroll" || value === "clip"
    );
}
